import i18nInst from "i18next";
import { useMemo, useState } from "react";
import useEvent from "react-use-event-hook";

import {
  getLangLocalStorage,
  Log,
  resolveAppLanguage,
  type SupportedLanguage,
} from "@ryde/shared";

import { ERROR_BOUNDARY_TEXTS } from "./error-boundary.constants";
import type {
  IErrorBoundaryFallbackProps,
  IErrorBoundaryTexts,
} from "./error-boundary.models";
import { formatErrorBoundaryReport } from "./error-boundary.utils";

interface IUseErrorBoundaryFallbackResult {
  fullReport: string;
  texts: IErrorBoundaryTexts;
  locale: SupportedLanguage;
  isRtl: boolean;
  isDetailsExpanded: boolean;
  handleLocaleChange: (locale: SupportedLanguage) => void;
  handleToggleDetails: () => void;
  handleCopyReport: () => Promise<void>;
  handleReloadPage: () => void;
}

export const useErrorBoundaryFallback = ({
  error,
  errorInfo,
}: IErrorBoundaryFallbackProps): IUseErrorBoundaryFallbackResult => {
  // Starts in the app language (localStorage → current i18n → default); the
  // switcher only changes the boundary texts, not the app language.
  const [locale, setLocale] = useState<SupportedLanguage>(() =>
    resolveAppLanguage({
      storedLanguage: getLangLocalStorage(),
      currentLanguage: i18nInst.language,
    }),
  );
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const texts = ERROR_BOUNDARY_TEXTS[locale];

  const fullReport = useMemo(
    () => formatErrorBoundaryReport({ error, errorInfo }),
    [error, errorInfo],
  );

  const handleLocaleChange = useEvent((nextLocale: SupportedLanguage) => {
    setLocale(nextLocale);
  });

  const handleToggleDetails = useEvent(() => {
    setIsDetailsExpanded(prev => !prev);
  });

  const handleCopyReport = useEvent(async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(fullReport);
      Log.infoAndToast(texts.reportCopied);
    } catch {
      Log.warn(texts.copyFailed);
    }
  });

  const handleReloadPage = useEvent(() => {
    window.location.reload();
  });

  return {
    fullReport,
    texts,
    locale,
    isRtl: locale === "he",
    isDetailsExpanded,
    handleLocaleChange,
    handleToggleDetails,
    handleCopyReport,
    handleReloadPage,
  };
};
